import { useEffect, useState, useRef } from 'react';
import { History, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import PriceHistoryModal from '@/components/PriceHistoryModal';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { Product } from '@/types';
import { genInternalBarcode } from '@/utils/barcode';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { useCreateProduct, useUpdateProduct, useCategories } from '@/hooks/useProducts';
import { productsService } from '@/services/products';
import { useSuppliers } from '@/hooks/useSuppliers';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { Calculator } from 'lucide-react';
import { formatPrice, formatCurrencyLive, parseCurrencyToRaw } from '@/utils/format';
import {
  Dimension,
  Unit,
  UNIT_OPTIONS_BY_DIMENSION,
  UNIT_LABEL_SHORT,
  toBase,
  fromBase,
  pricePerBaseFromDisplay,
  pricePerDisplayFromBase,
} from '@/utils/units';

// Genera un código de barras interno EAN-13 (prefijo 200 = uso interno de comercios).
// Único por producto; sirve para imprimir en la etiqueta y escanear en la venta.
// Funciones para calcular precios sugeridos según la fórmula de la categoría
const productSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  code: z.string().optional(),
  description: z.string().optional(),
  category_id: z.string().optional(),
  supplier_id: z.string().optional(),
  sale_price: z.string().optional(),     // Precio EFECTIVO ($/base_unit)
  pack_price: z.string().optional(),     // Precio de venta por caja (total)
  list_price: z.string().optional(),     // Precio TARJETA ($/base_unit)
  transfer_price: z.string().optional(), // Precio TRANSFERENCIA ($/base_unit)
  qr_price: z.string().optional(),       // Precio QR ($/base_unit)
  cost_price: z.string().optional(),
  freight_cost: z.string().optional(),
  tax_cost: z.string().optional(),
  commission_cost: z.string().optional(),
  current_stock: z.string().default('0'),
  minimum_stock: z.string().default('10'),
  track_stock: z.boolean().default(true),
  units_per_pack: z.string().default('1'),
  // Sistema de unidades
  dimension: z.enum(['count', 'weight']).default('count'),
  display_unit: z.enum(['unit', 'gram', 'kilogram']).default('unit'),
  expiration_date: z.string().optional(),
  batch_number: z.string().optional(),
  iva_rate: z.string().optional(),
});

type ProductFormData = z.infer<typeof productSchema>;

interface ProductFormProps {
  product?: Product | null;
  onSuccess: () => void;
  /** Código con el que arranca el alta (viene de escanear uno que no existía). */
  initialCode?: string;
}

export default function ProductForm({ product, onSuccess, initialCode }: ProductFormProps) {
  const { data: categories } = useCategories();
  const { data: suppliers } = useSuppliers();
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const queryClient = useQueryClient();

  // Imagen del producto (para la tienda online).
  const [imageUrl, setImageUrl] = useState<string | undefined>(product?.image_url || undefined);
  const [uploadingImg, setUploadingImg] = useState(false);
  const imgInputRef = useRef<HTMLInputElement>(null);
  // Para producto NUEVO: la imagen se elige antes de crear y se sube apenas se crea.
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | undefined>(undefined);
  const imgSrc = imageUrl ? `${import.meta.env.VITE_API_URL?.replace('/api/v1', '') || ''}${imageUrl}` : undefined;

  const handlePickNewImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingImageFile(file);
    setPendingPreview(URL.createObjectURL(file));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !product?.id) return;
    setUploadingImg(true);
    try {
      const updated = await productsService.uploadImage(product.id, file);
      setImageUrl(updated.image_url);
      queryClient.invalidateQueries({ queryKey: ['products'] });
    } catch (err) {
      console.error('Error subiendo imagen:', err);
    } finally {
      setUploadingImg(false);
      if (imgInputRef.current) imgInputRef.current.value = '';
    }
  };

  const handleImageDelete = async () => {
    if (!product?.id) return;
    try {
      await productsService.deleteImage(product.id);
      setImageUrl(undefined);
      queryClient.invalidateQueries({ queryKey: ['products'] });
    } catch (err) {
      console.error('Error borrando imagen:', err);
    }
  };

  // Para edicion: convertir valores base_unit del producto a display_unit para mostrar
  const productDefaults = product
    ? (() => {
        const dim: Dimension = (product.dimension as Dimension) || 'count';
        const dispUnit: Unit = (product.display_unit as Unit) || 'unit';
        const toDisplayPrice = (v?: number | null) =>
          v === null || v === undefined ? '' : pricePerDisplayFromBase(Number(v), dispUnit).toString();
        const toDisplayStock = (v?: number | null) =>
          v === null || v === undefined ? '' : fromBase(Number(v), dispUnit).toString();
        return {
          name: product.name,
          code: product.code || '',
          description: product.description || '',
          category_id: product.category_id?.toString() || '',
          supplier_id: product.supplier_id?.toString() || '',
          sale_price: toDisplayPrice(product.sale_price),
          pack_price: product.pack_price != null ? String(product.pack_price) : '',
          list_price: toDisplayPrice(product.list_price),
          transfer_price: toDisplayPrice(product.transfer_price),
          qr_price: toDisplayPrice(product.qr_price),
          cost_price: toDisplayPrice(product.cost_price),
          freight_cost: toDisplayPrice(product.freight_cost),
          tax_cost: toDisplayPrice(product.tax_cost),
          commission_cost: toDisplayPrice(product.commission_cost),
          current_stock: toDisplayStock(product.current_stock) || '0',
          minimum_stock: toDisplayStock(product.minimum_stock) || '0',
          track_stock: product.track_stock,
          units_per_pack: product.units_per_pack != null ? String(product.units_per_pack) : '1',
          dimension: dim,
          display_unit: dispUnit,
          expiration_date: product.expiration_date || '',
          batch_number: product.batch_number || '',
          iva_rate: product.iva_rate != null ? product.iva_rate.toString() : '',
        };
      })()
    : {
        // Viene cargado cuando el alta arrancó escaneando un código que no existía
        code: initialCode || '',
        track_stock: true,
        minimum_stock: '10',
        current_stock: '0',
        units_per_pack: '1',
        pack_price: '',
        dimension: 'count' as Dimension,
        display_unit: 'unit' as Unit,
        iva_rate: '', // productos nuevos arrancan SIN IVA (se tilda a mano si corresponde)
      };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: productDefaults,
  });

  // Pistola de código de barras dentro del ABM: el código va SIEMPRE al campo
  // "Código", sin importar dónde esté el foco. Estando acá adentro, escanear
  // significa "este es el código de este producto" y nunca "buscá este producto".
  const [codigoEscaneado, setCodigoEscaneado] = useState(false);
  useBarcodeScanner({
    onScan: (code) => {
      setValue('code', code, { shouldDirty: true });
      setCodigoEscaneado(true);
    },
  });

  const trackStock = watch('track_stock');
  const costPrice = watch('cost_price');
  const salePrice = watch('sale_price');
  const packPrice = watch('pack_price');
  const categoryId = watch('category_id');
  const supplierId = watch('supplier_id');
  const ivaRate = watch('iva_rate');
  const dimension = watch('dimension') as Dimension;
  const displayUnit = watch('display_unit') as Unit;
  const unitsPerPack = watch('units_per_pack');

  // Modo "compra por bulto": cargás el costo de la CAJA y el sistema lo divide por las
  // unidades para guardar el costo por unidad (que es lo que usa todo el sistema).
  const [byBulk, setByBulk] = useState<boolean>(() => Number(product?.units_per_pack ?? 1) > 1);
  const [boxCost, setBoxCost] = useState<string>(() => {
    const u = Number(product?.units_per_pack ?? 1);
    if (u > 1 && product?.cost_price != null) return String(Number((Number(product.cost_price) * u).toFixed(2)));
    return '';
  });

  const recomputeUnitFromBox = (box: string, units: string) => {
    const b = Number(box) || 0;
    const u = Math.max(1, Number(units) || 1);
    const perUnit = b > 0 ? b / u : 0;
    setValue('cost_price', perUnit ? String(Number(perUnit.toFixed(4))) : '', { shouldDirty: true });
  };

  const toggleByBulk = (on: boolean) => {
    setByBulk(on);
    if (on) {
      let u = Math.round(Number(watch('units_per_pack')) || 1);
      if (u < 2) { u = 12; setValue('units_per_pack', '12', { shouldDirty: true }); }
      const cp = Number(watch('cost_price')) || 0;
      setBoxCost(cp ? String(Number((cp * u).toFixed(2))) : '');
    } else {
      setValue('units_per_pack', '1', { shouldDirty: true });
      setBoxCost('');
    }
  };
  const unitSuffix = displayUnit ? `/${UNIT_LABEL_SHORT[displayUnit]}` : '';

  // Auto-prender IVA al elegir un proveedor con charges_iva=true (solo si iva_rate esta vacio)
  useEffect(() => {
    if (!supplierId || !suppliers) return;
    const sel = suppliers.find((s) => s.id.toString() === supplierId);
    if (sel && sel.charges_iva && (ivaRate === '' || ivaRate === undefined)) {
      setValue('iva_rate', '21');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  // Calculadora "compra a granel" (compré X kg → costo por unidad/kg/g)
  const [showBulkCalc, setShowBulkCalc] = useState(false);
  const [bulkKgQuantity, setBulkKgQuantity] = useState('');
  const [bulkKgPrice, setBulkKgPrice] = useState('');


  // Modal de historial de cambios de precio
  const [showHistory, setShowHistory] = useState(false);

  // Si dimension=count, forzar display_unit=unit. Si pasa a weight y display_unit no es valido, usar kilogram.
  useEffect(() => {
    if (dimension === 'count' && displayUnit !== 'unit') {
      setValue('display_unit', 'unit');
    } else if (dimension === 'weight' && displayUnit !== 'gram' && displayUnit !== 'kilogram') {
      setValue('display_unit', 'kilogram');
    }
  }, [dimension]);

  // Obtener la categoría seleccionada para usar su configuración de precios
  const selectedCategory = categoryId
    ? categories?.find((c) => c.id.toString() === categoryId)
    : null;

  // Cálculo de precio segun pricing_method (espejo del backend pricing_calc.py)
  const calcWithMethod = (
    cost: number,
    value: number,
    method: 'multiplier' | 'margin',
    roundTo: number,
  ): number => {
    if (cost <= 0 || value <= 0) return 0;
    let price: number;
    if (method === 'margin') {
      const denom = 1 - value / 100;
      if (denom <= 0) return 0;
      price = cost / denom;
    } else {
      price = cost * value;
    }
    if (roundTo && roundTo > 0) {
      price = Math.ceil(price / roundTo) * roundTo;
    } else {
      price = Math.round(price * 100) / 100;
    }
    return price;
  };

  // Costo final (incluye IVA + extras) desde los watch del form
  const landedCostForSuggest = (() => {
    const base = parseFloat(costPrice || '') || 0;
    const f = parseFloat(watch('freight_cost') || '') || 0;
    const t = parseFloat(watch('tax_cost') || '') || 0;
    const c = parseFloat(watch('commission_cost') || '') || 0;
    const ivaPct = ivaRate && ivaRate !== '' ? parseFloat(ivaRate) || 0 : 0;
    return base * (1 + ivaPct / 100) + f + t + c;
  })();

  const catMethod: 'multiplier' | 'margin' = (selectedCategory?.pricing_method as any) === 'margin' ? 'margin' : 'multiplier';
  const catCash = Number(selectedCategory?.cash_multiplier ?? 0);
  const catRoundTo = Number(selectedCategory?.price_round_to ?? 0);

  // Precio sugerido: landed cost × multiplicador de la categoría
  const suggestedSalePrice = landedCostForSuggest > 0 && catCash > 0
    ? calcWithMethod(landedCostForSuggest, catCash, catMethod, catRoundTo)
    : null;

  // Aplicar precio de venta sugerido (desde el costo y la categoría)
  const applySuggestedSalePrice = () => {
    if (suggestedSalePrice) {
      setValue('sale_price', suggestedSalePrice.toString());
    }
  };

  const onSubmit = async (data: ProductFormData) => {
    const dispUnit: Unit = (data.display_unit as Unit) || 'unit';
    // Convertir precios y stock de display_unit a base_unit antes de enviar
    const convertPrice = (v?: string) => {
      if (!v) return undefined;
      const num = Number(v);
      if (isNaN(num)) return undefined;
      return pricePerBaseFromDisplay(num, dispUnit);
    };
    const convertStock = (v?: string) => {
      if (v === undefined || v === '') return 0;
      const num = Number(v);
      if (isNaN(num)) return 0;
      return toBase(num, dispUnit);
    };

    // Precio de venta EXPLÍCITO: vacío -> null (borra el precio), "0" -> 0, si no el valor.
    // Se envía siempre (no undefined) para poder sacar/poner en 0 el precio.
    const salePriceStr = (data.sale_price ?? '').toString().trim();
    const salePriceOut: number | null = salePriceStr === ''
      ? null
      : (convertPrice(salePriceStr) ?? null);

    const payload = {
      name: data.name,
      // OJO con `|| undefined`: axios NO manda las claves undefined, asi que al
      // vaciar el campo el borrado no viajaba y el valor viejo quedaba. Va null,
      // que es lo que el backend usa para limpiar.
      code: data.code?.trim() ? data.code.trim() : null,
      description: data.description?.trim() ? data.description.trim() : null,
      category_id: data.category_id ? Number(data.category_id) : undefined,
      supplier_id: data.supplier_id ? Number(data.supplier_id) : undefined,
      // Precio único de venta. Mantenemos las columnas viejas sincronizadas al mismo
      // valor para compatibilidad (el sistema ya no distingue por método de pago).
      sale_price: salePriceOut,
      list_price: salePriceOut,
      transfer_price: salePriceOut,
      qr_price: salePriceOut,
      // Precio por caja: total de la caja (no por unidad). Vacío -> null.
      pack_price: (data.pack_price ?? '').toString().trim() === '' ? null : Number(parseCurrencyToRaw(data.pack_price as string)),
      cost_price: convertPrice(data.cost_price),  // $/base (NETO sin IVA)
      freight_cost: convertPrice(data.freight_cost) ?? null,
      tax_cost: convertPrice(data.tax_cost) ?? null,
      commission_cost: convertPrice(data.commission_cost) ?? null,
      iva_rate: data.iva_rate && data.iva_rate !== '' ? Number(data.iva_rate) : null,
      current_stock: convertStock(data.current_stock),
      minimum_stock: convertStock(data.minimum_stock),
      track_stock: data.track_stock,
      units_per_pack: Math.max(1, Math.round(Number(data.units_per_pack) || 1)),
      dimension: data.dimension,
      display_unit: data.display_unit,
      expiration_date: data.expiration_date || undefined,
      batch_number: data.batch_number || undefined,
    };

    try {
      if (product) {
        await updateProduct.mutateAsync({ id: product.id, data: payload });
      } else {
        const created = await createProduct.mutateAsync(payload);
        // Subir la imagen elegida al crear (si hay)
        if (pendingImageFile && created?.id) {
          try {
            await productsService.uploadImage(created.id, pendingImageFile);
            queryClient.invalidateQueries({ queryKey: ['products'] });
          } catch (err) {
            console.error('Error subiendo imagen del producto nuevo:', err);
          }
        }
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const categoryOptions = [
    { value: '', label: 'Sin categoría' },
    ...(categories?.map((c) => ({ value: c.id.toString(), label: c.name })) || []),
  ];

  const supplierOptions = [
    { value: '', label: 'Sin proveedor' },
    ...(suppliers?.map((s) => ({ value: s.id.toString(), label: s.name })) || []),
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Imagen del producto (tienda online) */}
      <div>
        <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-1.5">
          Imagen (tienda online)
        </label>
        {product?.id ? (
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
              {imgSrc ? (
                <img src={imgSrc} alt="" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="h-7 w-7 text-gray-300" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={imgInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              <button
                type="button"
                onClick={() => imgInputRef.current?.click()}
                disabled={uploadingImg}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-taupe-100 text-taupe-700 hover:bg-taupe-200 disabled:opacity-50"
              >
                <Upload className="h-4 w-4" />
                {uploadingImg ? 'Subiendo...' : imgSrc ? 'Cambiar imagen' : 'Subir imagen'}
              </button>
              {imgSrc && (
                <button
                  type="button"
                  onClick={handleImageDelete}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Quitar
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
              {pendingPreview ? (
                <img src={pendingPreview} alt="" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="h-7 w-7 text-gray-300" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={imgInputRef} type="file" accept="image/*" onChange={handlePickNewImage} className="hidden" />
              <button
                type="button"
                onClick={() => imgInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-taupe-100 text-taupe-700 hover:bg-taupe-200"
              >
                <Upload className="h-4 w-4" />
                {pendingPreview ? 'Cambiar imagen' : 'Elegir imagen'}
              </button>
              {pendingPreview && (
                <button
                  type="button"
                  onClick={() => { setPendingImageFile(null); setPendingPreview(undefined); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" /> Quitar
                </button>
              )}
              <p className="text-[11px] text-gray-400">Se sube al crear el producto.</p>
            </div>
          </div>
        )}
      </div>

      <Input
        label="Nombre *"
        {...register('name')}
        error={errors.name?.message}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Código (código de barras)</label>
          <div className="flex gap-2">
            <input
              {...register('code')}
              className={`flex-1 min-w-0 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                codigoEscaneado ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300'
              }`}
            />
            <button
              type="button"
              onClick={() => setValue('code', genInternalBarcode(), { shouldDirty: true })}
              className="px-3 py-2 text-sm font-medium text-taupe-700 bg-taupe-100 hover:bg-taupe-200 rounded-lg whitespace-nowrap flex-shrink-0"
            >
              Generar
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {codigoEscaneado
              ? '✓ Código tomado de la pistola.'
              : 'Escaneá con la pistola y se completa solo (no hace falta hacer clic acá). Si el producto no trae código, "Generar" crea uno interno (EAN-13) para la etiqueta.'}
          </p>
        </div>
      </div>

      <Input label="Descripción" {...register('description')} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Categoría"
          options={categoryOptions}
          {...register('category_id')}
        />
        <Select
          label="Proveedor"
          options={supplierOptions}
          {...register('supplier_id')}
        />
      </div>

      {/* Unidad de medida */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-3">
        <h3 className="font-medium text-gray-700">Unidad de medida</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <div className="flex gap-2">
              <label className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer flex-1 hover:bg-white transition-colors">
                <input
                  type="radio"
                  value="count"
                  {...register('dimension')}
                  className="text-primary-600"
                />
                <span className="text-sm">Cantidad (unidades)</span>
              </label>
              <label className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer flex-1 hover:bg-white transition-colors">
                <input
                  type="radio"
                  value="weight"
                  {...register('dimension')}
                  className="text-primary-600"
                />
                <span className="text-sm">Peso (a granel)</span>
              </label>
            </div>
          </div>
          <div>
            <Select
              label="Unidad mostrada"
              options={(UNIT_OPTIONS_BY_DIMENSION[dimension] || UNIT_OPTIONS_BY_DIMENSION.count).map(
                (o) => ({ value: o.value, label: o.label }),
              )}
              {...register('display_unit')}
              disabled={dimension === 'count'}
            />
          </div>
        </div>
        {dimension === 'count' && (
          <div className="text-xs text-gray-600 bg-white border border-gray-200 rounded-lg p-3">
            <strong>Cantidad</strong> es para productos vendidos como <em>paquetes individuales</em>:
            ej. "Nueces 20g", "Galletitas x12". Cada uno es 1 unidad.
            Si comprás materia prima por kg para armar paquetes, usá la <strong>calculadora de costo</strong> abajo.
          </div>
        )}
        {dimension === 'weight' && (
          <div className="text-xs text-gray-600 bg-white border border-gray-200 rounded-lg p-3">
            <strong>Peso (a granel)</strong> es para productos que vendés en cantidades variables:
            ej. harina suelta, fideos por gramo, fiambre al peso. El cliente pide cualquier cantidad.
            <span className="block mt-1 text-blue-700">
              Precio se ingresa por {displayUnit === 'kilogram' ? 'kg' : 'g'}; stock idem.
              Internamente todo se guarda por gramo.
            </span>
          </div>
        )}
      </div>

      {/* Sección de precios */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-700 flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Precios
          </h3>
          {dimension === 'weight' && (
            <button
              type="button"
              onClick={() => setShowBulkCalc(!showBulkCalc)}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <Calculator className="h-3.5 w-3.5" />
              {showBulkCalc ? 'Cerrar calculadora' : 'Calcular costo desde compra a granel'}
            </button>
          )}
        </div>

        {/* Calculadora granel: compré X kg por $Y total → costo por kg/gramo */}
        {showBulkCalc && dimension === 'weight' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <p className="text-xs text-blue-900">
              <strong>Compra a granel</strong>: poné cuántos kg compraste y el <strong>precio total</strong> que pagaste. Te calcula el costo por {UNIT_LABEL_SHORT[displayUnit]}.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Cantidad comprada (kg)"
                type="number"
                step="0.01"
                placeholder="22"
                value={bulkKgQuantity}
                onChange={(e) => setBulkKgQuantity(e.target.value)}
              />
              <Input
                label="Precio total pagado ($)"
                type="number"
                step="0.01"
                placeholder="30000"
                value={bulkKgPrice}
                onChange={(e) => setBulkKgPrice(e.target.value)}
              />
            </div>
            {(() => {
              const kgQty = parseFloat(bulkKgQuantity) || 0;
              const totalCost = parseFloat(bulkKgPrice) || 0;
              if (kgQty <= 0 || totalCost <= 0) return null;
              const costPerKg = totalCost / kgQty;
              const costPerDisplay = displayUnit === 'kilogram' ? costPerKg : costPerKg / 1000; // /g
              return (
                <div className="bg-white border border-blue-300 rounded-md p-3 space-y-1 text-sm">
                  <p className="text-gray-600">Total: <strong>${totalCost.toLocaleString('es-AR')}</strong> por <strong>{kgQty} kg</strong></p>
                  <p className="text-blue-900 font-medium">
                    Costo por {UNIT_LABEL_SHORT[displayUnit]}: <strong>${costPerDisplay.toFixed(displayUnit === 'gram' ? 4 : 2)}</strong>
                    {displayUnit !== 'kilogram' && <span className="text-gray-500 font-normal"> (= ${costPerKg.toFixed(2)}/kg)</span>}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setValue('cost_price', costPerDisplay.toString(), { shouldDirty: true });
                      setShowBulkCalc(false);
                    }}
                    className="mt-1 inline-flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded"
                  >
                    Aplicar al costo
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        {/* Costo: por unidad o por bulto/caja */}
        <div className="space-y-3">
          {dimension === 'count' && (
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={byBulk}
                onChange={(e) => toggleByBulk(e.target.checked)}
                className="rounded border-taupe-300 text-taupe-600 focus:ring-taupe-400"
              />
              <span className="font-medium text-taupe-800">Compro por bulto / caja</span>
            </label>
          )}

          {byBulk && dimension === 'count' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-1.5">Unidades por caja</label>
                <input
                  type="number"
                  min="2"
                  step="1"
                  value={unitsPerPack ?? ''}
                  onChange={(e) => { setValue('units_per_pack', e.target.value, { shouldDirty: true }); recomputeUnitFromBox(boxCost, e.target.value); }}
                  onFocus={(e) => e.target.select()}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-1.5">Costo de la caja (neto, sin IVA)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={formatCurrencyLive(boxCost)}
                    onChange={(e) => { const raw = parseCurrencyToRaw(e.target.value); setBoxCost(raw); recomputeUnitFromBox(raw, unitsPerPack || '1'); }}
                    onFocus={(e) => e.target.select()}
                    className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400"
                  />
                </div>
                <p className="text-xs text-gray-600 mt-1">= <strong>{costPrice ? formatPrice(costPrice) : '$0'}</strong> por unidad</p>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-1.5">Costo base{unitSuffix} (neto, sin IVA)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={formatCurrencyLive(costPrice ?? '')}
                  onChange={(e) => setValue('cost_price', parseCurrencyToRaw(e.target.value), { shouldDirty: true })}
                  className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400"
                />
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Lo que figura en la factura del proveedor (sin IVA).</p>
            </div>
          )}
          {/* campos siempre presentes en el form */}
          <input type="hidden" {...register('cost_price')} />
          <input type="hidden" {...register('units_per_pack')} />
        </div>

        {/* IVA — registramos el field siempre para que setValue persista al toggle */}
        <input type="hidden" {...register('iva_rate')} />
        <div className="flex items-start gap-3 p-3 rounded-lg bg-cream-50 border border-cream-200">
          <input
            type="checkbox"
            checked={!!ivaRate && ivaRate !== ''}
            onChange={(e) =>
              setValue('iva_rate', e.target.checked ? '21' : '', { shouldDirty: true })
            }
            className="mt-1 rounded border-taupe-300 text-taupe-600 focus:ring-taupe-400"
          />
          <div className="flex-1">
            <label className="text-sm font-medium text-taupe-900 cursor-pointer block">
              Aplicar IVA al costo
            </label>
            <p className="text-xs text-taupe-600 mb-2">
              Al estar tildado, el sistema suma el % de IVA al costo base para calcular el costo final.
            </p>
            {!!ivaRate && ivaRate !== '' && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={ivaRate}
                  onChange={(e) => setValue('iva_rate', e.target.value, { shouldDirty: true })}
                  onFocus={(e) => e.target.select()}
                  className="w-24 px-2 py-1 border border-cream-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400"
                />
                <span className="text-xs text-taupe-600">%</span>
                <span className="text-xs text-taupe-500">(estándar AR: 21%)</span>
              </div>
            )}
          </div>
        </div>

        {/* Costos compuestos (flete + impuestos + comisiones) */}
        <details className="group">
          <summary className="cursor-pointer text-xs text-blue-700 hover:text-blue-900 font-medium select-none">
            + Agregar flete / impuestos / comisiones (costos compuestos)
          </summary>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2 p-3 bg-blue-50/50 border border-blue-200 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-blue-800 mb-1">Flete{unitSuffix}</label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                {...register('freight_cost')}
                className="w-full px-3 py-2 border border-blue-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-800 mb-1">Impuestos{unitSuffix}</label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                {...register('tax_cost')}
                className="w-full px-3 py-2 border border-blue-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <p className="text-[11px] text-blue-700 mt-0.5">No recuperables.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-800 mb-1">Comisiones{unitSuffix}</label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                {...register('commission_cost')}
                className="w-full px-3 py-2 border border-blue-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
        </details>

        {/* Costo final (landed cost) */}
        {(() => {
          const base = parseFloat(costPrice || '') || 0;
          const f = parseFloat(watch('freight_cost') || '') || 0;
          const t = parseFloat(watch('tax_cost') || '') || 0;
          const c = parseFloat(watch('commission_cost') || '') || 0;
          const ivaPct = ivaRate && ivaRate !== '' ? parseFloat(ivaRate) || 0 : 0;
          const ivaAmount = base * (ivaPct / 100);
          const landed = base + ivaAmount + f + t + c;
          const hasExtras = f > 0 || t > 0 || c > 0 || ivaAmount > 0;
          if (!base) return null;
          return (
            <div className={`text-sm rounded-lg p-2 ${hasExtras ? 'bg-cream-50 border border-cream-200 text-taupe-900' : 'bg-taupe-50 border border-taupe-200 text-taupe-700'}`}>
              <strong>Costo final{unitSuffix}: ${landed.toLocaleString('es-AR', { maximumFractionDigits: 4 })}</strong>
              {hasExtras && (
                <span className="block text-xs mt-0.5 text-taupe-600">
                  ${base.toLocaleString('es-AR')} base
                  {ivaAmount > 0 && <> + ${ivaAmount.toLocaleString('es-AR', { maximumFractionDigits: 2 })} IVA ({ivaPct}%)</>}
                  {(f + t + c) > 0 && <> + ${(f + t + c).toLocaleString('es-AR', { maximumFractionDigits: 4 })} extras</>}
                  {' '}({(((landed - base) / base) * 100).toFixed(1)}% sobre base)
                </span>
              )}
            </div>
          );
        })()}

        {/* Precio de venta (único) */}
        <p className="text-xs uppercase tracking-wide font-semibold text-taupe-600 mt-3">Precio de venta</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-emerald-700 mb-1">Precio de venta{unitSuffix}</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={formatCurrencyLive(salePrice ?? '')}
                onChange={(e) => setValue('sale_price', parseCurrencyToRaw(e.target.value), { shouldDirty: true })}
                className="w-full pl-7 pr-3 py-2 border border-emerald-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>
            <input type="hidden" {...register('sale_price')} />
          </div>

          {dimension === 'count' && Number(unitsPerPack) > 1 && (
            <div>
              <label className="block text-xs font-medium text-emerald-700 mb-1">Precio de venta por caja ({Number(unitsPerPack)} u)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={formatCurrencyLive(packPrice ?? '')}
                  onChange={(e) => setValue('pack_price', parseCurrencyToRaw(e.target.value), { shouldDirty: true })}
                  className="w-full pl-7 pr-3 py-2 border border-emerald-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
              </div>
              <input type="hidden" {...register('pack_price')} />
              <p className="text-xs text-gray-500 mt-1">
                Total de la caja. Si llevan la caja entera, se cobra este precio
                {packPrice && Number(parseCurrencyToRaw(packPrice)) > 0 && Number(unitsPerPack) > 0
                  ? ` (= ${formatPrice(Number(parseCurrencyToRaw(packPrice)) / Number(unitsPerPack))} por unidad)`
                  : ''}.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mt-2">
          {suggestedSalePrice && (!salePrice || parseFloat(salePrice) !== suggestedSalePrice) && (
            <button
              type="button"
              onClick={applySuggestedSalePrice}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <Calculator className="h-3 w-3" />
              Precio sugerido: ${suggestedSalePrice.toLocaleString('es-AR')}
            </button>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-2">
          Si dejás el precio vacío y elegís categoría, se calcula automáticamente desde el costo.
          El descuento por pago en efectivo se aplica manualmente en el momento de la venta.
          {dimension === 'weight' && (
            <span className="block mt-1 text-blue-600">
              El precio se ingresa por {UNIT_LABEL_SHORT[displayUnit]}, se guarda por gramo internamente.
            </span>
          )}
        </p>

        {/* Margen real (calculado sobre landed cost = base*(1+iva%) + flete + impuestos + comisiones) */}
        {(() => {
          const base = parseFloat(costPrice || '') || 0;
          const f = parseFloat(watch('freight_cost') || '') || 0;
          const t = parseFloat(watch('tax_cost') || '') || 0;
          const c = parseFloat(watch('commission_cost') || '') || 0;
          const ivaPct = ivaRate && ivaRate !== '' ? parseFloat(ivaRate) || 0 : 0;
          const landed = base * (1 + ivaPct / 100) + f + t + c;
          const cash = parseFloat(salePrice || '');
          if (!landed || !cash || landed <= 0 || cash <= 0) return null;
          const margin = ((cash - landed) / cash) * 100;
          const markup = ((cash - landed) / landed) * 100;
          const marginMinEff =
            selectedCategory?.margin_min != null ? Number(selectedCategory.margin_min) : null;
          const isLow = marginMinEff != null && margin < marginMinEff;
          return (
            <div className={`mt-2 text-xs rounded-lg p-2 border ${isLow ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
              {isLow ? '⚠️ ' : '✓ '}
              Margen real (efectivo, sobre costo final): <strong>{margin.toFixed(1)}%</strong> · Markup: <strong>{markup.toFixed(0)}%</strong>
              {marginMinEff != null && <span className="ml-2 opacity-70">(mínimo {marginMinEff}% según categoría)</span>}
            </div>
          );
        })()}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          {...register('track_stock')}
          className="rounded border-gray-300 text-primary-600"
        />
        <span className="text-sm">Controlar stock</span>
      </div>

      {trackStock && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={`Stock actual${displayUnit ? ` (${UNIT_LABEL_SHORT[displayUnit]})` : ''}`}
            type="number"
            step={dimension === 'weight' ? '0.01' : '1'}
            {...register('current_stock')}
          />
          <Input
            label={`Stock mínimo${displayUnit ? ` (${UNIT_LABEL_SHORT[displayUnit]})` : ''}`}
            type="number"
            step={dimension === 'weight' ? '0.01' : '1'}
            {...register('minimum_stock')}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Fecha de vencimiento"
          type="date"
          {...register('expiration_date')}
        />
        <Input label="Número de lote" {...register('batch_number')} />
      </div>

      <div className="flex items-center justify-between pt-4">
        {product ? (
          <button
            type="button"
            onClick={() => setShowHistory(true)}
            className="inline-flex items-center gap-1.5 text-sm text-taupe-600 hover:text-taupe-800 font-medium"
          >
            <History className="h-4 w-4" />
            Ver historial de precios
          </button>
        ) : <span />}
        <Button type="submit" loading={createProduct.isPending || updateProduct.isPending}>
          {product ? 'Guardar Cambios' : 'Crear Producto'}
        </Button>
      </div>

      {product && (
        <PriceHistoryModal
          productId={product.id}
          productName={product.name}
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
        />
      )}
    </form>
  );
}
